import React, { useEffect, useState, useMemo } from 'react';
import { motion, useMotionValue, useSpring, useTransform, useScroll, AnimatePresence } from 'framer-motion';
import { 
  Code, 
  Database, 
  Layout, 
  Smartphone, 
  Globe, 
  Cpu, 
  ShoppingCart, 
  Activity, 
  Shield, 
  Zap,
  Stethoscope,
  Cloud,
  Brain,
  ShoppingBag,
  HardHat,
  Hammer,
  GraduationCap,
  BookOpen,
  Landmark,
  CreditCard,
  Truck,
  Package,
  Server,
  Megaphone,
  TrendingUp,
  X,
  Settings,
  Sliders,
  ArrowRight,
  Sparkles,
  CheckCircle2,
  LayoutDashboard,
  Lock,
  ShieldCheck
} from 'lucide-react';
import { ParallaxBackground } from './ParallaxBackground';
import { Particles } from './Particles';
import { CircularTechRings } from './CircularTechRings';
import { useAuth } from '../src/contexts/AuthContext';

type AnimationStyle = 'fade-in' | 'slide-up' | 'zoom-in';

interface AnimationConfig {
  style: AnimationStyle;
  speed: number;
  delay: number;
}

const CharacterReveal: React.FC<{ text: string; delay?: number; className?: string }> = ({ text, delay = 0, className = "" }) => {
  return (
    <motion.span
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] }}
      className={`inline-block ${className}`}
    >
      {text}
    </motion.span>
  );
};

const MantraEyebrow: React.FC = () => {
  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
      className="flex items-center justify-center gap-2 sm:gap-3.5 mb-2 w-full max-w-2xl px-2"
    >
      {/* Tapered accent line left */}
      <span className="h-[1px] w-6 sm:w-12 bg-gradient-to-r from-transparent via-[#ffd885]/70 to-[#d8aa68]" />

      <div className="inline-flex items-center gap-2 sm:gap-2.5 px-3.5 py-1.5 rounded-full border border-[#d8aa68]/30 bg-[#1a0c06]/80 backdrop-blur-md shadow-[0_4px_20px_rgba(0,0,0,0.6)]">
        <span className="text-[10px] sm:text-[11px] font-mono text-[#ecd8be] uppercase tracking-[0.2em] sm:tracking-[0.25em] font-semibold">
          The Brown Pelican Man
        </span>
      </div>

      {/* Tapered accent line right */}
      <span className="h-[1px] w-6 sm:w-12 bg-gradient-to-l from-transparent via-[#ffd885]/70 to-[#d8aa68]" />
    </motion.div>
  );
};

export const Hero: React.FC = () => {
  const [showScroll, setShowScroll] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const { profile, user, isPlatformAdmin } = useAuth();
  const isAdmin = profile?.role === 'admin' || isPlatformAdmin(user?.email || undefined);
  
  const [config, setConfig] = useState<AnimationConfig>({
    style: 'slide-up',
    speed: 1.6,
    delay: 0.5
  });

  const { scrollY } = useScroll();

  // Detect mobile device to disable heavy transforms and prevent scroll glitches / disappearing section
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Mouse position state
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  // Smooth spring animation for mouse movement - tuned for cinematic weight
  const springConfig = { damping: 40, stiffness: 200, mass: 0.5 };
  const mouseX = useSpring(x, springConfig);
  const mouseY = useSpring(y, springConfig);

  // Text layer moves slightly opposite to mouse (foreground parallax)
  const textX = useTransform(mouseX, [-0.5, 0.5], ["-25px", "25px"]);
  const textY = useTransform(mouseY, [-0.5, 0.5], ["-25px", "25px"]);

  // Scroll animations: Desktop gently fades, while mobile stays visible without glitching to blank
  const desktopOpacity = useTransform(scrollY, [0, 900], [1, 0.2]);
  const desktopScale = useTransform(scrollY, [0, 900], [1, 0.96]);

  // Background Icons Data - Expanded to cover all domains
  const backgroundIcons = useMemo(() => [
    // Tech & SaaS
    { Icon: Code, top: '15%', left: '10%', size: 48, speed: 0.1, delay: 0, label: 'Code - Software Development' },
    { Icon: Database, top: '25%', left: '85%', size: 64, speed: 0.25, delay: 0.2, label: 'Database - Data Architecture' },
    { Icon: Server, top: '65%', left: '15%', size: 56, speed: 0.15, delay: 0.4, label: 'Server - Infrastructure' },
    { Icon: Smartphone, top: '75%', left: '80%', size: 40, speed: 0.3, delay: 0.6, label: 'Smartphone - Mobile Apps' },
    { Icon: Globe, top: '10%', left: '60%', size: 32, speed: 0.05, delay: 0.8, label: 'Globe - Web Platforms' },
    { Icon: Cpu, top: '45%', left: '5%', size: 72, speed: 0.4, delay: 1.0, label: 'CPU - Systems Engineering' },
    
    // E-commerce & Retail
    { Icon: ShoppingCart, top: '55%', left: '90%', size: 48, speed: 0.2, delay: 1.2, label: 'ShoppingCart - E-commerce' },
    { Icon: ShoppingBag, top: '35%', left: '75%', size: 40, speed: 0.12, delay: 1.6, label: 'ShoppingBag - Retail' },
    
    // Healthcare
    { Icon: Activity, top: '85%', left: '30%', size: 64, speed: 0.35, delay: 1.4, label: 'Activity - Health Monitoring' },
    { Icon: Stethoscope, top: '20%', left: '35%', size: 56, speed: 0.18, delay: 1.8, label: 'Stethoscope - Medical Tech' },
    
    // Construction
    { Icon: HardHat, top: '12%', left: '25%', size: 44, speed: 0.22, delay: 0.5, label: 'HardHat - Construction Tech' },
    { Icon: Hammer, top: '80%', left: '10%', size: 52, speed: 0.08, delay: 1.1, label: 'Hammer - Building Solutions' },
    
    // Education
    { Icon: GraduationCap, top: '40%', left: '92%', size: 48, speed: 0.28, delay: 0.9, label: 'GraduationCap - EdTech' },
    { Icon: BookOpen, top: '60%', left: '25%', size: 36, speed: 0.15, delay: 1.5, label: 'BookOpen - Learning Platforms' },
    
    // Fintech
    { Icon: Landmark, top: '30%', left: '50%', size: 50, speed: 0.05, delay: 0.7, label: 'Landmark - Fintech' },
    { Icon: CreditCard, top: '70%', left: '65%', size: 42, speed: 0.32, delay: 1.3, label: 'CreditCard - Payments' },
    
    // Performance Marketing & Social Ads
    { Icon: Megaphone, top: '22%', left: '72%', size: 48, speed: 0.18, delay: 0.85, label: 'Megaphone - Meta & Google Ads' },
    { Icon: TrendingUp, top: '78%', left: '22%', size: 54, speed: 0.28, delay: 1.15, label: 'TrendingUp - ROAS & Growth' },

    // Logistics
    { Icon: Truck, top: '90%', left: '55%', size: 58, speed: 0.25, delay: 1.7, label: 'Truck - Logistics' },
    { Icon: Package, top: '5%', left: '80%', size: 38, speed: 0.1, delay: 0.3, label: 'Package - Supply Chain' },
    
    // AI & Innovation
    { Icon: Brain, top: '50%', left: '40%', size: 80, speed: -0.1, delay: 0.1, label: 'Brain - AI & Machine Learning' },
    { Icon: Zap, top: '85%', left: '5%', size: 46, speed: 0.45, delay: 1.9, label: 'Zap - Performance' },
  ], []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      // Normalize mouse coordinates to -0.5 to 0.5
      x.set((e.clientX / window.innerWidth) - 0.5);
      y.set((e.clientY / window.innerHeight) - 0.5);
    };

    window.addEventListener('mousemove', handleMouseMove);
    
    // Delay scroll indicator appearance
    const timer = setTimeout(() => setShowScroll(true), 3500);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      clearTimeout(timer);
    };
  }, [x, y]);

  const getVariants = (style: AnimationStyle) => {
    switch (style) {
      case 'fade-in':
        return {
          initial: { opacity: 0 },
          animate: { opacity: 1 }
        };
      case 'zoom-in':
        return {
          initial: { opacity: 0, scale: 0.95 },
          animate: { opacity: 1, scale: 1 }
        };
      case 'slide-up':
      default:
        return {
          initial: { opacity: 0, y: 30 },
          animate: { opacity: 1, y: 0 }
        };
    }
  };

  const variants = getVariants(config.style);

  return (
    <section 
      className="relative min-h-[100dvh] w-full flex flex-col items-center justify-center bg-transparent md:perspective-[1200px] pt-20 sm:pt-28 md:pt-48 pb-10 sm:pb-16 md:pb-20 overflow-hidden transform-gpu"
    >
      
      {/* Subtle Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0a0a0a] to-[#050505] opacity-80 pointer-events-none" />

      {/* Floating Particles */}
      <Particles />

      {/* Parallax Background Icons */}
      <ParallaxBackground 
        icons={backgroundIcons} 
        mouseX={mouseX} 
        mouseY={mouseY} 
        scrollY={scrollY} 
      />

      {/* Main Content - Layered Reveal without disappearing on mobile scroll */}
      <motion.div 
        style={{ 
            x: isMobile ? 0 : textX, 
            y: isMobile ? 0 : textY, 
            opacity: isMobile ? 1 : desktopOpacity,
            scale: isMobile ? 1 : desktopScale
        }}
        className="relative z-10 text-center px-3 sm:px-6 max-w-7xl mx-auto flex flex-col items-center justify-center gap-2.5 sm:gap-4 w-full overflow-visible will-change-[transform,opacity]"
      >

        {/* Elegant Animated Mantra Eyebrow */}
        <MantraEyebrow />

        {/* Primary Tripartite Hero Stage: Left Text Wing, Center Large Circle Logo, Right Text Wing */}
        <div className="relative w-full max-w-7xl mx-auto my-1 sm:my-3 md:my-6 px-1 sm:px-4">
          <div className="relative z-10 grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] items-center gap-2 sm:gap-6 md:gap-8 lg:gap-12 w-full">
            
            {/* =========================================================================
                LEFT WING: "IF YOU HAVE A GREAT BUSINESS STORY,"
               ========================================================================= */}
            <motion.div 
              initial={{ opacity: 0, x: -35 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="flex flex-col items-center md:items-end text-center md:text-right w-full"
            >
              {/* Left Headline */}
              <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-display font-bold text-white uppercase tracking-tight leading-[1.12] drop-shadow-[0_4px_24px_rgba(0,0,0,0.9)]">
                <span className="block text-gray-200">
                  <CharacterReveal text="IF YOU HAVE A" delay={0.1} />
                </span>
                <span className="block text-transparent bg-clip-text bg-gradient-to-r md:bg-gradient-to-l from-white via-[#ffe5a3] to-[#ffd885] drop-shadow-[0_0_24px_rgba(255,216,133,0.3)]">
                  <CharacterReveal text="GREAT BUSINESS" delay={0.25} />
                </span>
                <span className="block text-[#ffe0a3]">
                  <CharacterReveal text="STORY," delay={0.4} />
                </span>
              </h1>
            </motion.div>

            {/* =========================================================================
                CENTER: PROMINENT LARGE CIRCLE MEDALLION WITH BRAND LOGO
               ========================================================================= */}
            <motion.div 
              initial={{ scale: 0.85, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="relative w-32 h-32 sm:w-44 sm:h-44 md:w-60 md:h-60 lg:w-72 lg:h-72 xl:w-80 xl:h-80 aspect-square shrink-0 mx-auto my-1 sm:my-3 md:my-0 select-none flex items-center justify-center"
            >
              {/* Concentric Rotating Curvy Tech Rings - Precisely Centered on this Logo */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[240px] sm:w-[440px] md:w-[720px] lg:w-[940px] pointer-events-none -z-10 opacity-25 md:opacity-35 overflow-hidden md:overflow-visible">
                <CircularTechRings showCenterHub={false} />
              </div>

              {/* Radial Glowing Ambient Backlight */}
              <div className="absolute inset-[-12px] sm:inset-[-30px] rounded-full bg-[radial-gradient(circle_at_center,rgba(216,170,104,0.32)_0%,rgba(139,69,19,0.18)_50%,transparent_72%)] blur-lg sm:blur-2xl pointer-events-none animate-pulse" />

              {/* Precision Compass Radial Coordinates (N, S, E, W Rivets) */}
              <div className="absolute -top-2 sm:-top-4 left-1/2 -translate-x-1/2 w-2 sm:w-2.5 h-2 sm:h-2.5 rounded-full bg-gradient-to-b from-[#ffe5a3] to-[#b88942] border border-[#2b1407] shadow-sm z-20" />
              <div className="absolute -bottom-2 sm:-bottom-4 left-1/2 -translate-x-1/2 w-2 sm:w-2.5 h-2 sm:h-2.5 rounded-full bg-gradient-to-b from-[#ffe5a3] to-[#b88942] border border-[#2b1407] shadow-sm z-20" />
              <div className="absolute -left-2 sm:-left-4 top-1/2 -translate-y-1/2 w-2 sm:w-2.5 h-2 sm:h-2.5 rounded-full bg-gradient-to-b from-[#ffe5a3] to-[#b88942] border border-[#2b1407] shadow-sm z-20" />
              <div className="absolute -right-2 sm:-right-4 top-1/2 -translate-y-1/2 w-2 sm:w-2.5 h-2 sm:h-2.5 rounded-full bg-gradient-to-b from-[#ffe5a3] to-[#b88942] border border-[#2b1407] shadow-sm z-20" />

              {/* The Enlarged Center Circle Bezel */}
              <div 
                className="relative w-full h-full aspect-square rounded-full p-1.5 sm:p-2.5 md:p-3.5 border-2 sm:border-3 md:border-4 border-[#caa062] bg-gradient-to-b from-[#4d2714] via-[#32170b] to-[#140803] shadow-[0_24px_70px_rgba(0,0,0,0.98),0_0_36px_rgba(216,170,104,0.4),inset_0_2px_6px_rgba(255,255,255,0.25)] flex items-center justify-center transition-all duration-300 hover:border-[#ffe0a3]"
              >
                {/* Fine Stitched Leather Inner Perimeter Ring */}
                <div className="absolute inset-1 sm:inset-1.5 md:inset-2 rounded-full aspect-square border border-dashed border-[#d8aa68]/50 pointer-events-none" />

                {/* Pelican Brand Logo - Fully Contained without any edge clipping */}
                <img 
                  src="/brand_logo.png?v=4" 
                  alt="The Brown Pelican Man Logo" 
                  referrerPolicy="no-referrer"
                  className="w-full h-full object-contain rounded-full drop-shadow-[0_8px_24px_rgba(0,0,0,0.95)] select-none hover:scale-105 transition-transform duration-500"
                />
              </div>
            </motion.div>

            {/* =========================================================================
                RIGHT WING: "I'VE GOT A VALUABLE SOLUTION FOR YOU."
               ========================================================================= */}
            <motion.div 
              initial={{ opacity: 0, x: 35 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="flex flex-col items-center md:items-start text-center md:text-left w-full"
            >
              {/* Right Headline */}
              <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-display font-bold text-white uppercase tracking-tight leading-[1.12] drop-shadow-[0_4px_24px_rgba(0,0,0,0.9)]">
                <span className="block text-gray-200">
                  <CharacterReveal text="I’VE GOT A" delay={0.2} />
                </span>
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#ffd885] via-[#ffffff] to-[#d89a42] drop-shadow-[0_0_24px_rgba(255,216,133,0.35)]">
                  <CharacterReveal text="VALUABLE" delay={0.35} />
                </span>
                <span className="block text-[#ffe0a3]">
                  <CharacterReveal text="SOLUTION FOR YOU." delay={0.5} />
                </span>
              </h1>
            </motion.div>

          </div>
        </div>

        {/* 8 Domain Disciplines Scope Bar - Clean Wrap on Mobile */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.7 }}
          className="inline-flex flex-col sm:flex-row items-center gap-1 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-1.5 rounded-2xl sm:rounded-full bg-[#200f07]/90 border border-[#d8aa68]/35 text-[#ffe0a3] text-[9.5px] sm:text-xs md:text-sm font-mono font-medium tracking-wide shadow-[0_4px_16px_rgba(0,0,0,0.6)] my-1 w-full sm:w-auto max-w-4xl text-center"
        >
          <div className="flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-[#ffd885] animate-ping shrink-0" />
            <span className="sm:hidden font-semibold tracking-wider text-[#ffd885] uppercase text-[9.5px]">Scope of Disciplines</span>
          </div>
          <span className="hidden sm:inline">DEVELOPER · ARCHITECT · ANALYST · RESEARCHER · STRATEGIST · CONSULTANT · INNOVATOR · MARKETER</span>
          <div className="flex sm:hidden flex-wrap items-center justify-center gap-x-1.5 gap-y-0.5 text-[9px] text-[#ecd8be]">
            <span>DEVELOPER</span>•<span>ARCHITECT</span>•<span>ANALYST</span>•<span>RESEARCHER</span>•<span>STRATEGIST</span>•<span>CONSULTANT</span>•<span>INNOVATOR</span>•<span>MARKETER</span>
          </div>
        </motion.div>

        {/* Persona Authority & Mission Description */}
        <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="flex flex-col items-center gap-1.5 sm:gap-3 max-w-3xl px-2 sm:px-4 mt-1 sm:mt-2"
        >
            <div className="inline-flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-0.5 sm:py-1 rounded-full bg-white/[0.04] border border-white/10 text-gray-300 text-[10.5px] sm:text-sm font-space tracking-wider uppercase">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                <span>Founder &amp; CEO, PopingUp • Principal Systems Architect</span>
            </div>
            <p className="text-xs sm:text-base md:text-lg text-gray-300 font-light leading-relaxed text-center max-w-2xl px-1">
                Turning high-complexity ideas into scalable ventures — engineering custom <span className="text-[#ffe0a3] font-medium">web &amp; mobile apps</span>, mission-critical <span className="text-[#ffe0a3] font-medium">admin dashboards</span>, authenticated <span className="text-[#ffe0a3] font-medium">login portals</span>, and enterprise-grade <span className="text-[#ffe0a3] font-medium">security</span>.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-1.5 sm:gap-2 pt-1.5 max-w-2xl">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#200f07]/90 border border-[#d8aa68]/35 text-[#ffe0a3] text-[10px] sm:text-xs font-mono font-medium shadow-sm">
                <Smartphone className="w-3 h-3 text-[#d8aa68]" />
                Web &amp; Mobile Apps
              </span>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#200f07]/90 border border-[#d8aa68]/35 text-[#ffe0a3] text-[10px] sm:text-xs font-mono font-medium shadow-sm">
                <LayoutDashboard className="w-3 h-3 text-[#d8aa68]" />
                Admin Dashboards
              </span>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#200f07]/90 border border-[#d8aa68]/35 text-[#ffe0a3] text-[10px] sm:text-xs font-mono font-medium shadow-sm">
                <Lock className="w-3 h-3 text-[#d8aa68]" />
                Login Portals
              </span>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#200f07]/90 border border-[#d8aa68]/35 text-[#ffe0a3] text-[10px] sm:text-xs font-mono font-medium shadow-sm">
                <ShieldCheck className="w-3 h-3 text-[#d8aa68]" />
                Enterprise Security &amp; RBAC
              </span>
            </div>
        </motion.div>

        {/* Action CTAs */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="flex flex-col sm:flex-row flex-wrap justify-center items-center gap-2.5 sm:gap-4 mt-3 sm:mt-6 w-full max-w-sm sm:max-w-none px-2 sm:px-4"
        >
            <motion.button 
              whileHover={{ scale: 1.04, y: -2 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => document.getElementById('capabilities')?.scrollIntoView({ behavior: 'smooth' })}
              className="relative w-full sm:w-auto px-6 sm:px-9 py-3 sm:py-4 rounded-full overflow-hidden transition-all duration-300 group shadow-[0_8px_25px_rgba(0,0,0,0.8),inset_0_1px_2px_rgba(255,255,255,0.25)] border border-[#d8aa68]/50 hover:border-[#ffe0a3]/80 hover:shadow-[0_10px_30px_rgba(216,170,104,0.35)] flex items-center justify-center gap-2"
              style={{
                background: 'linear-gradient(180deg, #4d2714 0%, #381b0e 38%, #241108 72%, #140803 100%)'
              }}
            >
              {/* Fine gold leather stitch line */}
              <span className="absolute inset-[3px] rounded-full border border-dashed border-[#d8aa68]/35 pointer-events-none group-hover:border-[#ffe0a3]/70 transition-colors"></span>
              <span className="relative z-10 text-[11px] sm:text-xs font-mono uppercase tracking-widest font-bold text-[#ffe0a3] group-hover:text-white transition-colors flex items-center gap-2">
                <span>Explore Capabilities</span>
                <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#d8aa68] group-hover:text-white group-hover:translate-x-1 transition-all" />
              </span>
            </motion.button>
            <motion.button 
              whileHover={{ scale: 1.04, y: -2 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              className="relative w-full sm:w-auto px-6 sm:px-9 py-3 sm:py-4 rounded-full overflow-hidden transition-all duration-300 group shadow-[0_6px_20px_rgba(0,0,0,0.6)] border border-white/20 hover:border-[#d8aa68]/70 hover:bg-white/[0.06] flex items-center justify-center"
            >
              <span className="relative z-10 text-[11px] sm:text-xs font-mono uppercase tracking-widest font-bold text-white group-hover:text-[#ffe0a3] transition-colors">
                Schedule an Appointment
              </span>
            </motion.button>
        </motion.div>
      </motion.div>

      {/* Animation Customizer */}
      {isAdmin && (
        <div className="fixed top-24 right-6 z-[60]">
          <motion.button
            whileHover={{ scale: 1.1, rotate: 90 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => setShowSettings(!showSettings)}
            className="p-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-full text-white shadow-xl"
          >
            <Settings size={20} />
          </motion.button>

          <AnimatePresence>
            {showSettings && (
              <motion.div
                initial={{ opacity: 0, x: 20, scale: 0.9 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{ opacity: 0, x: 20, scale: 0.9 }}
                className="absolute top-16 right-0 w-72 bg-[#0A0A0A]/90 backdrop-blur-2xl border border-white/10 rounded-3xl p-6 shadow-2xl"
              >
                <div className="flex items-center justify-between mb-6">
                  <h4 className="text-xs font-mono text-white uppercase tracking-widest flex items-center gap-2">
                    <Sliders size={14} className="text-blue-500" />
                    Animation Settings
                  </h4>
                  <button onClick={() => setShowSettings(false)} className="text-gray-500 hover:text-white transition-colors">
                    <X size={16} />
                  </button>
                </div>

                <div className="space-y-6">
                  {/* Style Selector */}
                  <div className="space-y-3">
                    <label className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">Style</label>
                    <div className="grid grid-cols-3 gap-2">
                      {(['fade-in', 'slide-up', 'zoom-in'] as AnimationStyle[]).map((style) => (
                        <button
                          key={style}
                          onClick={() => setConfig({ ...config, style })}
                          className={`py-2 px-1 rounded-lg text-[9px] font-mono uppercase tracking-tighter transition-all border ${
                            config.style === style 
                            ? 'bg-white text-black border-white' 
                            : 'bg-white/5 text-gray-400 border-white/10 hover:border-white/30'
                          }`}
                        >
                          {style.replace('-', ' ')}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Speed Slider */}
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <label className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">Speed (s)</label>
                      <span className="text-[10px] font-mono text-blue-400">{config.speed}s</span>
                    </div>
                    <input 
                      type="range" 
                      min="0.5" 
                      max="3" 
                      step="0.1" 
                      value={config.speed}
                      onChange={(e) => setConfig({ ...config, speed: parseFloat(e.target.value) })}
                      className="w-full h-1 bg-white/10 rounded-lg appearance-none cursor-pointer accent-blue-500"
                    />
                  </div>

                  {/* Delay Slider */}
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <label className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">Delay (s)</label>
                      <span className="text-[10px] font-mono text-blue-400">{config.delay}s</span>
                    </div>
                    <input 
                      type="range" 
                      min="0" 
                      max="2" 
                      step="0.1" 
                      value={config.delay}
                      onChange={(e) => setConfig({ ...config, delay: parseFloat(e.target.value) })}
                      className="w-full h-1 bg-white/10 rounded-lg appearance-none cursor-pointer accent-blue-500"
                    />
                  </div>

                  <div className="pt-4 border-t border-white/5">
                    <p className="text-[9px] font-mono text-gray-600 leading-relaxed italic">
                      * Settings apply in real-time to the main heading reveal.
                    </p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Scroll Indicator */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: showScroll ? 1 : 0 }}
        transition={{ duration: 1 }}
        className="hidden md:flex absolute bottom-8 left-1/2 -translate-x-1/2 flex-col items-center gap-2 pointer-events-none"
      >
        <span className="text-[10px] font-mono text-gray-600 tracking-widest uppercase">DISCOVER</span>
        <div className="w-[1px] h-12 bg-gradient-to-b from-gray-500 to-transparent"></div>
      </motion.div>
    </section>
  );
};
